import { Shield, Key, AlertTriangle, CheckCircle, Clock, Zap, TrendingUp } from 'lucide-react';

// Gemini API key config — credits expire, not keys. Update creditsExpireAt when new accounts created.
const GEMINI_KEYS = [
  {
    index: 1,
    label: 'Key 1',
    account: 'patstweevoet@gmail.com',
    creditsExpireAt: new Date('2026-05-14'),
    budgetAlert: 100,
    status: 'active', // active | pending | postponed
    note: 'Also used by Online Directories AI',
  },
  {
    index: 2,
    label: 'Key 2',
    account: 'fanzechodev@gmail.com',
    creditsExpireAt: null, // set when account created (~May 9–11)
    createBy: new Date('2026-05-11'),
    budgetAlert: 100,
    status: 'pending',
    note: 'Create ~May 9–11, just before Key 1 credits expire',
  },
  {
    index: 3,
    label: 'Key 3',
    account: 'fanzecho@gmail.com',
    creditsExpireAt: null,
    budgetAlert: 100,
    status: 'postponed',
    note: 'Create when Key 2 runs low — post-launch',
  },
];

function daysUntil(date) {
  if (!date) return null;
  const diff = date - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function KeyExpiryBar({ days, max = 90 }) {
  if (days === null) return null;
  const pct = Math.max(0, Math.min(100, (days / max) * 100));
  const color = days <= 7 ? '#ff4444' : days <= 20 ? '#ffaa00' : '#00ff41';
  return (
    <div className="h-1.5 bg-gray-800 rounded-full mt-1.5 overflow-hidden">
      <div style={{ width: `${pct}%`, backgroundColor: color, height: '100%', borderRadius: '9999px', transition: 'width 0.5s' }} />
    </div>
  );
}

function ApiKeyCard({ keyConfig }) {
  const days = daysUntil(keyConfig.creditsExpireAt);
  const daysToCreate = daysUntil(keyConfig.createBy);

  const statusColor = {
    active: '#00ff41',
    pending: '#ffaa00',
    postponed: '#555',
  }[keyConfig.status];

  const statusLabel = {
    active: 'ACTIVE',
    pending: 'NOT YET CREATED',
    postponed: 'POSTPONED',
  }[keyConfig.status];

  return (
    <div className="bg-[#111] border border-[#222] rounded-lg p-4 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4" style={{ color: statusColor }} />
          <span className="text-white font-bold text-sm">{keyConfig.label}</span>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ color: statusColor, border: `1px solid ${statusColor}22`, background: `${statusColor}11` }}>
          {statusLabel}
        </span>
      </div>

      <p className="text-gray-500 text-xs">{keyConfig.account}</p>

      {keyConfig.status === 'active' && days !== null && (
        <>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-400">Credits expire May 14</span>
            <span className="text-xs font-bold" style={{ color: days <= 7 ? '#ff4444' : days <= 20 ? '#ffaa00' : '#00ff41' }}>
              {days}d left
            </span>
          </div>
          <KeyExpiryBar days={days} max={90} />
          <p className="text-[11px] text-gray-600 mt-0.5">⚠ {keyConfig.note}</p>
        </>
      )}

      {keyConfig.status === 'pending' && (
        <>
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-400">Create by May 11</span>
            {daysToCreate !== null && (
              <span className="text-xs font-bold text-[#ffaa00]">{daysToCreate}d to act</span>
            )}
          </div>
          <p className="text-[11px] text-gray-600 mt-0.5">💡 {keyConfig.note}</p>
        </>
      )}

      {keyConfig.status === 'postponed' && (
        <p className="text-[11px] text-gray-600 mt-1">💡 {keyConfig.note}</p>
      )}

      <div className="flex items-center gap-1 mt-1">
        <span className="text-[10px] text-gray-600">Budget alert:</span>
        <span className="text-[10px] text-[#00d1ff]">${keyConfig.budgetAlert} USD</span>
        {keyConfig.status === 'active' && <CheckCircle className="w-3 h-3 text-[#00ff41] ml-1" />}
      </div>
    </div>
  );
}

function SecurityEventRow({ event }) {
  const severityColor = { critical: '#ff4444', warning: '#ffaa00', info: '#00d1ff' }[event.severity] || '#555';
  const eventLabels = {
    jailbreak_attempt: '🛡 Jailbreak attempt',
    rate_limit_exceeded: '⚡ Rate limit exceeded',
    cost_anomaly_user: '💸 Cost anomaly',
    system_traffic_spike: '🚨 Traffic spike',
    rls_audit_heartbeat: '✅ RLS audit heartbeat',
    rls_audit_findings: '⚠ RLS audit findings',
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: severityColor }} />
        <span className="text-xs text-gray-300">{eventLabels[event.event_type] || event.event_type}</span>
        {event.metadata?.userId && (
          <span className="text-[10px] text-gray-600 font-mono">{String(event.metadata.userId).slice(0, 8)}…</span>
        )}
        {event.metadata?.ip && (
          <span className="text-[10px] text-gray-600 font-mono">{event.metadata.ip}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-600">{timeAgo(event.created_at)}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: severityColor, background: `${severityColor}18` }}>
          {event.severity}
        </span>
      </div>
    </div>
  );
}

export function SecurityPanel({ securityData }) {
  const { events = [], totalConversations = 0, unresolvedCount = 0, geminiKeyInUse = 1 } = securityData || {};

  const criticalCount = events.filter(e => e.severity === 'critical').length;
  const warningCount = events.filter(e => e.severity === 'warning').length;
  const recentEvents = events.slice(0, 8);

  return (
    <div className="mt-6">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4">
        <Shield className="w-5 h-5 text-[#00d1ff]" />
        <h2 className="text-[#00d1ff] font-bold tracking-wider text-sm uppercase">Security & API Health</h2>
        {unresolvedCount > 0 && (
          <span className="text-[10px] bg-[#ff444422] text-[#ff4444] border border-[#ff444444] px-2 py-0.5 rounded font-bold">
            {unresolvedCount} UNRESOLVED
          </span>
        )}
      </div>

      {/* Top row: key status + gemini usage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {GEMINI_KEYS.map(k => <ApiKeyCard key={k.index} keyConfig={k} />)}
      </div>

      {/* Gemini usage summary */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-[#111] border border-[#222] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-[#00ff41]" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">Total AI Calls</span>
          </div>
          <p className="text-2xl font-bold text-white">{totalConversations.toLocaleString()}</p>
          <p className="text-[11px] text-gray-600 mt-1">All-time Zecho conversations</p>
        </div>

        <div className="bg-[#111] border border-[#222] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-[#00d1ff]" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">Active Key</span>
          </div>
          <p className="text-2xl font-bold text-[#00ff41]">KEY {geminiKeyInUse}</p>
          <p className="text-[11px] text-gray-600 mt-1">patstweevoet · credits expire May 14</p>
        </div>

        <div className="bg-[#111] border border-[#222] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-[#ffaa00]" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">Security Events</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: criticalCount > 0 ? '#ff4444' : warningCount > 0 ? '#ffaa00' : '#00ff41' }}>
            {criticalCount > 0 ? `${criticalCount} CRITICAL` : warningCount > 0 ? `${warningCount} WARN` : 'ALL CLEAR'}
          </p>
          <p className="text-[11px] text-gray-600 mt-1">{events.length} events in last 48h</p>
        </div>
      </div>

      {/* Security event log */}
      <div className="bg-[#111] border border-[#222] rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400 text-xs uppercase tracking-wider">Recent Security Events</span>
          </div>
          <span className="text-[10px] text-gray-600">Auto-refreshes every 60s · Populated by Trigger.dev jobs</span>
        </div>

        {recentEvents.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle className="w-8 h-8 text-[#00ff41] mx-auto mb-2" />
            <p className="text-[#00ff41] text-sm font-semibold">All clear</p>
            <p className="text-gray-600 text-xs mt-1">No security events in the last 48h</p>
            <p className="text-gray-700 text-[10px] mt-2">Trigger.dev jobs run at 03:00 · 07:00 · 13:00 · 19:00</p>
          </div>
        ) : (
          <div>
            {recentEvents.map(event => <SecurityEventRow key={event.id} event={event} />)}
          </div>
        )}
      </div>
    </div>
  );
}
